import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, CreditCard, TrendingUp, AlertCircle, Wallet, ChevronRight, Trophy } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { admin, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsData, commRes] = await Promise.all([
          api.getDashboardStats(token),
          fetch(`${API}/admin/commissions/summary`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);
        setStats(statsData);
        setCommissions(commRes);
      } catch (err) {
        toast({ title: 'Erreur', description: err.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [token, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total Abonnés',
      value: stats?.total_subscribers || 0,
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      title: 'Abonnements Actifs',
      value: stats?.active_subscriptions || 0,
      icon: CreditCard,
      color: 'bg-green-500',
    },
    {
      title: 'MRR Estimé',
      value: `CHF ${stats?.mrr || 0}`,
      icon: TrendingUp,
      color: 'bg-purple-500',
    },
    {
      title: 'En Attente',
      value: stats?.pending_subscriptions || 0,
      icon: AlertCircle,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Bienvenue {admin?.first_name} {admin?.last_name}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardDescription className="text-sm font-medium">
                  {stat.title}
                </CardDescription>
                <div className={`${stat.color} p-2 rounded-lg`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Customers */}
        <Card>
          <CardHeader>
            <CardTitle>Derniers Abonnés</CardTitle>
            <CardDescription>Les 5 derniers clients inscrits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_customers?.length > 0 ? (
                stats.recent_customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {customer.first_name} {customer.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{customer.email}</p>
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(customer.created_at).toLocaleDateString('fr-CH')}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">Aucun client récent</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Attention Required */}
        <Card>
          <CardHeader>
            <CardTitle>Abonnements Nécessitant Attention</CardTitle>
            <CardDescription>En attente ou suspendus</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.attention_subscriptions?.length > 0 ? (
                stats.attention_subscriptions.map((sub) => (
                  <div
                    key={sub.id}
                    className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {sub.customer?.first_name} {sub.customer?.last_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {sub.plan_code} - CHF {sub.monthly_price}/mois
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        sub.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {sub.status === 'pending' ? 'En attente' : 'Suspendu'}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">
                  Aucun abonnement nécessitant attention
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commissions Block */}
      <Card data-testid="dashboard-commissions-block">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="text-indigo-600" size={20} />
              Commissions Vendeurs
            </CardTitle>
            <CardDescription>
              Mois en cours · cliquez pour gérer chaque vendeur
            </CardDescription>
          </div>
          <button
            onClick={() => navigate('/admin/sellers')}
            className="text-sm text-indigo-600 hover:underline inline-flex items-center gap-1"
            data-testid="dashboard-link-sellers"
          >
            Voir tous les vendeurs <ChevronRight size={14} />
          </button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="rounded-lg border bg-indigo-50/50 p-4" data-testid="kpi-commissions-month">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Total ce mois</p>
              <p className="text-2xl font-bold text-indigo-700 mt-1">
                CHF {Number(commissions?.total_month_chf || 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{commissions?.count_month || 0} contrat{(commissions?.count_month || 0) > 1 ? 's' : ''}</p>
            </div>
            <div className="rounded-lg border bg-yellow-50 p-4" data-testid="kpi-commissions-pending">
              <p className="text-xs text-gray-600 uppercase tracking-wide">À verser</p>
              <p className="text-2xl font-bold text-yellow-700 mt-1">
                CHF {Number(commissions?.total_pending_chf || 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{commissions?.count_pending || 0} commission{(commissions?.count_pending || 0) > 1 ? 's' : ''} en attente</p>
            </div>
            <div className="rounded-lg border bg-amber-50/50 p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide flex items-center gap-1">
                <Trophy size={11} className="text-amber-600" /> Top 3 du mois
              </p>
              {commissions?.top_sellers?.length > 0 ? (
                <ul className="mt-1 space-y-0.5 text-sm">
                  {commissions.top_sellers.map((t, idx) => (
                    <li key={t.seller_code} className="flex justify-between" data-testid={`top-seller-${idx}`}>
                      <span><strong>{idx + 1}.</strong> {t.seller_full_name || t.seller_code}</span>
                      <span className="font-mono text-gray-700">CHF {Number(t.amount_chf).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 mt-1">Aucune commission ce mois.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
